import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '../types';

interface NavbarProps {
  cartCount: number;
  onCartClick: () => void;
  onLogoClick: () => void;
  isAdmin?: boolean;
  currentUser: User | null;
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
  cartCount, 
  onCartClick, 
  onLogoClick, 
  isAdmin, 
  currentUser,
  onLogout
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // 현재 활성화된 영역 판단 로직
  const isShopActive = location.pathname.startsWith('/shop') || location.pathname.startsWith('/product');
  const isBrandActive = location.pathname === '/homepage';
  const isCompetitionsActive = location.pathname.startsWith('/competitions');
  
  // 💡 수정됨: 통합자료실 활성화 조건을 기존 정상 작동하던 코드로 복구 (정확히 일치할 때만 활성화)
  const isLibraryActive = location.pathname === '/library';

  // 💡 수정됨: 기존 Navbar에 있던 로컬 스토리지 캐시 삭제 로직 복구
  const handleLogout = () => {
    localStorage.removeItem('makerstory_user_cache');
    onLogout();
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        {/* Logo Section */}
        <div 
          className="flex items-center gap-2 cursor-pointer group shrink-0"
          onClick={() => navigate('/')}
        >
          <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-black text-lg md:text-xl shadow-lg shadow-indigo-100 group-hover:rotate-12 transition-transform">
            M
          </div>
          <span className="text-base md:text-xl font-black tracking-tighter text-gray-900 italic hidden sm:inline">
            MAKER STORY
          </span>
        </div>

        {/* Navigation Menu */}
        <nav className="ml-auto flex gap-0.5 sm:gap-1 md:gap-1 text-[10px] sm:text-xs md:text-sm font-black text-gray-500 items-center overflow-x-auto scrollbar-hide whitespace-nowrap px-1">
          <button 
            onClick={() => navigate('/shop')} 
            className={`transition ${isShopActive ? 'text-red-600 font-black' : 'hover:text-indigo-600'}`}
          >
            쇼핑홈
          </button>
          <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans">｜</span>
          
          <button 
            onClick={() => navigate('/homepage')} 
            className={`transition ${isBrandActive ? 'text-red-600 font-black' : 'hover:text-indigo-600'}`}
          >
            브랜드스토리
          </button>
          <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans">｜</span>
          
          <button 
            onClick={() => navigate('/competitions')} 
            className={`transition ${isCompetitionsActive ? 'text-red-600 font-black' : 'hover:text-indigo-600'}`}
          >
            대회&자격증
          </button>
          <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans">｜</span>
          
          {/* 통합자료실 버튼 */}
          <button 
            onClick={() => navigate('/library')} 
            className={`transition ${isLibraryActive ? 'text-red-600 font-black' : 'hover:text-indigo-600'}`}
          >
            통합자료실
          </button>
          
          {currentUser ? (
            <div className="flex items-center gap-0.5 sm:gap-1 md:gap-1">
              <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans">｜</span>
              <button onClick={() => navigate('/mypage?tab=orders')} className="hover:text-indigo-600 transition">주문내역</button>
              <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans">｜</span>
              <button onClick={() => navigate('/mypage?tab=profile')} className="hover:text-indigo-600 transition">정보수정</button>
              <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans mx-0">｜</span>
              <div className="flex items-center gap-0.5 md:gap-1">
                <span className="text-gray-900 font-black truncate max-w-[50px] sm:max-w-none">{currentUser.name}님</span>
                <button onClick={handleLogout} className="p-1 md:p-1 bg-gray-100 rounded-lg hover:text-red-500 transition">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3 md:w-3.5 md:h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-0.5 sm:gap-1 md:gap-1">
              <span className="text-gray-300 font-normal text-[8px] md:text-[10px] font-sans">｜</span>
              <button onClick={() => navigate('/login')} className="hover:text-indigo-600 transition">로그인</button>
            </div>
          )}

          <button 
            onClick={() => navigate('/admin')} 
            className={`ml-1 px-2 py-1 md:px-2.5 md:py-1.5 rounded-lg transition text-[10px] md:text-xs font-black uppercase shrink-0 ${
              isAdmin ? 'bg-gray-900 text-white shadow-md shadow-gray-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
          >
            A
          </button>
        </nav>

        {/* Cart Button */}
        <div className="flex items-center gap-4 shrink-0">
          <button 
            id="navbar-cart-btn"
            onClick={onCartClick}
            className={`relative p-2 md:p-2.5 bg-gray-50 text-gray-900 rounded-2xl hover:bg-indigo-600 hover:text-white transition shadow-sm ${
              cartCount > 0 ? 'animate-cart-red-blink ring-2 ring-red-500/20 shadow-red-100' : ''
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 md:w-5 md:h-5 text-[8px] md:text-[10px] font-black text-white bg-red-600 border-2 border-white rounded-full">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes cart-red-blink {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0px rgba(239, 68, 68, 0); }
          50% { transform: scale(1.15); box-shadow: 0 0 20px rgba(239, 68, 68, 0.6); border-color: rgba(239, 68, 68, 0.4); }
        }
        .animate-cart-red-blink {
          animation: cart-red-blink 1.5s infinite ease-in-out;
          color: #dc2626 !important;
          background-color: #fef2f2 !important;
        }
      `}</style>
    </header>
  );
};

export default Navbar;